import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Client } from "./dietitianData";
import { clientsApi } from "../../api";
import DietitianTopbar from "./DietitianTopbar";
import classes from "./ClientList.module.scss";

type Filter = "all" | "active" | "inactive" | "pending";

const Spinner = () => (
  <div className={classes.spinner_wrap}>
    <div className={classes.spinner} />
  </div>
);

const ClientList = () => {
  const navigate = useNavigate();
  const [clients,  setClients]  = useState<Client[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [filter,   setFilter]   = useState<Filter>("all");
  const [search,   setSearch]   = useState("");
  const [showAdd,  setShowAdd]  = useState(false);
  const [form, setForm] = useState({
    name: "", age: "", gender: "F", phone: "", email: "", goal: "",
    startWeight: "",
  });

  // ── Fetch clients ─────────────────────────────────────
  useEffect(() => {
    clientsApi.list()
      .then(data => setClients(data.clients))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const displayed = clients.filter(c => {
    const matchFilter = filter === "all" || c.status === filter;
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
                        (c.goal ?? "").toLowerCase().includes(search.toLowerCase()) ||
                        c.email.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const progressPct = (c: Client) => {
    if (!c.startWeight || !c.currentWeight || !c.idealWeight) return 0;
    const total = c.startWeight - c.idealWeight;
    const done  = c.startWeight - c.currentWeight;
    if (total <= 0) return 0;
    return Math.round(Math.max(0, Math.min(100, (done / total) * 100)));
  };

  const handleFormChange = (k: string, v: string) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const handleAddClient = async () => {
    if (!form.name || !form.email) return;
    setSaving(true);
    try {
      // Create a stub client record directly in Firestore via the API
      const newClient = {
        name:          form.name,
        email:         form.email,
        age:           form.age          ? Number(form.age)          : undefined,
        gender:        form.gender       || undefined,
        phone:         form.phone        || undefined,
        goal:          form.goal         || undefined,
        startWeight:   form.startWeight  ? Number(form.startWeight)  : undefined,
        status:        "pending",
        role:          "client",
        unlockedWeeks: ["Week 1"],
        nextApt:       null,
        nextAptType:   "",
      };
      // POST to a dedicated create-client endpoint
      const data = await clientsApi.create(newClient);
      // Refresh list
      const refreshed = await clientsApi.list();
      setClients(refreshed.clients);
      setShowAdd(false);
      setForm({ name: "", age: "", gender: "F", phone: "", email: "", goal: "", startWeight: "" });
      if (data.id) navigate(`/dietitian/clients/${data.id}`);
    } catch (err) {
      console.error("Add client failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const activeCount  = clients.filter(c => c.status === "active").length;
  const pendingCount = clients.filter(c => c.status === "pending").length;

  return (
    <>
      <DietitianTopbar
        title="Clients"
        subtitle={loading ? "Loading…" : `${activeCount} active${pendingCount > 0 ? ` · ${pendingCount} pending` : ""}`}
        actions={
          <button className={classes.add_btn} onClick={() => setShowAdd(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Client
          </button>
        }
      />

      {/* Content */}
      <div className={classes.content}>
        {/* Toolbar */}
        <div className={classes.toolbar}>
          <div className={classes.search_wrap}>
            <svg className={classes.search_icon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className={classes.search}
              placeholder="Search by name or goal…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className={classes.filter_bar}>
            {(["all","active","inactive","pending"] as Filter[]).map(f => (
              <button
                key={f}
                className={`${classes.filter_tab} ${filter === f ? classes.filter_active : ""}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? <Spinner /> : (
          <div className={classes.client_grid}>
            {displayed.map(c => (
              <div key={c.id} className={`${classes.client_card} ${c.status === "pending" ? classes.card_pending : ""}`} onClick={() => navigate(`/dietitian/clients/${c.id}`)}>
                <div className={classes.card_top}>
                  <div className={`${classes.client_avatar} ${c.status === "inactive" ? classes.avatar_inactive : ""} ${c.status === "pending" ? classes.avatar_pending : ""}`}>
                    {c.initials ?? c.name.slice(0,2).toUpperCase()}
                  </div>
                  <div className={classes.client_info}>
                    <div className={classes.client_name}>{c.name}</div>
                    <div className={classes.client_meta}>
                      {c.status === "pending"
                        ? c.email
                        : `${c.age} yrs · ${c.gender === "F" ? "Female" : "Male"}`}
                    </div>
                  </div>
                  <span className={`${classes.status_badge} ${c.status === "active" ? classes.badge_active : c.status === "pending" ? classes.badge_pending : classes.badge_inactive}`}>
                    {c.status}
                  </span>
                </div>

                {c.status === "pending" ? (
                  <div className={classes.pending_notice}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Profile incomplete — fill in at first appointment
                  </div>
                ) : (
                  <>
                    <div className={classes.goal_tag}>{c.goal}</div>
                    <div className={classes.weight_row}>
                      <div className={classes.weight_item}>
                        <span className={classes.weight_val}>{c.startWeight ?? "—"}</span>
                        <span className={classes.weight_lbl}>Start (kg)</span>
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                      <div className={classes.weight_item}>
                        <span className={classes.weight_val}>{c.currentWeight ?? "—"}</span>
                        <span className={classes.weight_lbl}>Current (kg)</span>
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                      <div className={classes.weight_item}>
                        <span className={classes.weight_val}>{c.idealWeight ?? "—"}</span>
                        <span className={classes.weight_lbl}>Ideal (kg)</span>
                      </div>
                    </div>
                    <div className={classes.progress_wrap}>
                      <div className={classes.progress_bar}>
                        <div className={classes.progress_fill} style={{ width: `${progressPct(c)}%` }} />
                      </div>
                      <span className={classes.progress_pct}>{progressPct(c)}%</span>
                    </div>
                  </>
                )}

                <div className={classes.card_footer}>
                  <span className={classes.next_apt}>
                    {c.nextApt ? `📅 ${c.nextApt}` : "No upcoming appointment"}
                  </span>
                  {c.status !== "pending" && (
                    <span className={classes.weeks_unlocked}>
                      {(c.unlockedWeeks ?? []).length}/4 weeks
                    </span>
                  )}
                </div>
              </div>
            ))}

            {displayed.length === 0 && (
              <p className={classes.empty}>No clients match your search.</p>
            )}
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      {showAdd && (
        <div className={classes.modal_overlay} onClick={() => setShowAdd(false)}>
          <div className={classes.modal_box} onClick={e => e.stopPropagation()}>
            <div className={classes.modal_header}>
              <h2 className={classes.modal_title}>Add New Client</h2>
              <button className={classes.modal_close} onClick={() => setShowAdd(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className={classes.form_grid}>
              <div className={classes.field}>
                <label className={classes.label}>Full Name *</label>
                <input className={classes.input} placeholder="e.g. Sarah Mansour" value={form.name} onChange={e => handleFormChange("name", e.target.value)} />
              </div>
              <div className={classes.field}>
                <label className={classes.label}>Age</label>
                <input className={classes.input} type="number" placeholder="e.g. 28" value={form.age} onChange={e => handleFormChange("age", e.target.value)} />
              </div>
              <div className={classes.field}>
                <label className={classes.label}>Gender</label>
                <select className={classes.input} value={form.gender} onChange={e => handleFormChange("gender", e.target.value)}>
                  <option value="F">Female</option>
                  <option value="M">Male</option>
                </select>
              </div>
              <div className={classes.field}>
                <label className={classes.label}>Phone</label>
                <input className={classes.input} placeholder="+961 70 XXX XXX" value={form.phone} onChange={e => handleFormChange("phone", e.target.value)} />
              </div>
              <div className={`${classes.field} ${classes.field_full}`}>
                <label className={classes.label}>Email *</label>
                <input className={classes.input} type="email" placeholder="client@email.com" value={form.email} onChange={e => handleFormChange("email", e.target.value)} />
              </div>
              <div className={`${classes.field} ${classes.field_full}`}>
                <label className={classes.label}>Goal</label>
                <input className={classes.input} placeholder="e.g. Weight Loss, Muscle Gain…" value={form.goal} onChange={e => handleFormChange("goal", e.target.value)} />
              </div>
              <div className={classes.field}>
                <label className={classes.label}>Start Weight (kg)</label>
                <input className={classes.input} type="number" placeholder="e.g. 80" value={form.startWeight} onChange={e => handleFormChange("startWeight", e.target.value)} />
              </div>

            </div>
            <div className={classes.modal_actions}>
              <button className={classes.cancel_btn} onClick={() => setShowAdd(false)} disabled={saving}>Cancel</button>
              <button className={classes.save_btn} onClick={handleAddClient} disabled={saving || !form.name || !form.email}>
                {saving ? <span className={classes.btn_spinner} /> : "Add Client"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientList;
